import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../lib/api';
import { useAuth } from '../lib/AuthContext';
import { format } from 'date-fns';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Users, DollarSign, HardDrive, Activity, ScanLine, Bell,
  BarChart3, CreditCard, UserCog, Layers, Heart, Search,
  Edit, Trash2, Ban, Check, ChevronDown, X, Shield, Key,
  FileText, BookOpen, MessageSquare, Plus, Eye, EyeOff, Save,
  Mail, Send, RefreshCw, Link2, AlertCircle, CheckCircle, Loader2
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { toast } from 'sonner';

const fmt = (n) => new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD' }).format(n || 0);

// ── Edit User Modal ──
function EditUserModal({ user: editUser, plans, onClose, onSave }) {
  const [role, setRole] = useState(editUser?.role || 'user');
  const [status, setStatus] = useState(editUser?.status || 'active');
  const [plan, setPlan] = useState(editUser?.plan || 'individual');
  const [saving, setSaving] = useState(false);

  const planOptions = [
    { value: 'individual', label: 'Individual (Free)' },
    { value: 'basic', label: 'Basic ($4.95/mo)' },
    { value: 'standard', label: 'Standard ($14.95/mo)' },
  ];

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave({ role, status, plan, plan_status: 'active' });
      onClose();
    } catch (e) { console.error('Save user error:', e); }
    setSaving(false);
  };

  const handleSetPlan = async () => {
    try {
      await api.put(`/api/admin/users/${editUser.id}/plan`, { plan, plan_status: 'active' });
      toast.success(`Plan set to ${plan}`);
    } catch (e) { toast.error('Failed to set plan'); console.error(e); }
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-testid="edit-user-modal">
      <motion.div initial={{ scale: 0.95 }} animate={{ scale: 1 }} className="bg-[#131d35] rounded-2xl w-full max-w-md max-h-[90vh] flex flex-col overflow-hidden border border-white/10 shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between p-6 pb-4 flex-shrink-0">
          <h2 className="text-lg font-bold text-white">Edit User</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-white transition-colors"><X className="w-5 h-5" /></button>
        </div>

        <div className="px-6 pb-6 overflow-y-auto flex-1 space-y-5">
          {/* User Info Card */}
          <div className="flex items-center gap-3 bg-white/5 rounded-xl p-4 border border-white/5">
            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center text-white font-bold text-lg flex-shrink-0">
              {editUser?.name?.[0]?.toUpperCase() || 'U'}
            </div>
            <div>
              <p className="font-semibold text-white">{editUser?.name}</p>
              <p className="text-sm text-slate-400">{editUser?.email}</p>
            </div>
          </div>

          {/* Role */}
          <div className="space-y-2">
            <label className="text-sm font-semibold text-white">Role</label>
            <div className="relative">
              <select
                value={role}
                onChange={(e) => setRole(e.target.value)}
                className="w-full appearance-none bg-[#1e293b] border border-white/10 rounded-xl px-4 py-3 text-white text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/50 cursor-pointer"
                data-testid="edit-user-role-select"
              >
                <option value="user">User</option>
                <option value="admin">Admin</option>
              </select>
              <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
            </div>
          </div>

          {/* Status */}
          <div className="space-y-2">
            <label className="text-sm font-semibold text-white">Status</label>
            <div className="relative">
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                className="w-full appearance-none bg-[#1e293b] border border-white/10 rounded-xl px-4 py-3 text-white text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/50 cursor-pointer"
                data-testid="edit-user-status-select"
              >
                <option value="active">Active</option>
                <option value="disabled">Disabled</option>
              </select>
              <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
            </div>
          </div>

          {/* Subscription Plan */}
          <div className="space-y-2">
            <label className="text-sm font-semibold text-white">Subscription Plan</label>
            <div className="flex gap-2">
              <div className="relative flex-1">
                <select
                  value={plan}
                  onChange={(e) => setPlan(e.target.value)}
                  className="w-full appearance-none bg-[#1e293b] border border-white/10 rounded-xl px-4 py-3 text-white text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/50 cursor-pointer"
                  data-testid="edit-user-plan-select"
                >
                  {planOptions.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
                <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
              </div>
              <button
                onClick={handleSetPlan}
                className="px-5 py-3 bg-emerald-500 hover:bg-emerald-600 text-white rounded-xl text-sm font-semibold transition-colors flex-shrink-0"
                data-testid="edit-user-set-plan-button"
              >
                Set
              </button>
            </div>
            <p className="text-xs text-slate-500">Current: {editUser?.plan || 'individual'} · {editUser?.plan_status || 'active'}</p>
          </div>

          {/* Temporary Access */}
          <div className="space-y-2">
            <label className="text-sm font-semibold text-white">Temporary Access</label>
            <button className="w-full flex items-center gap-2.5 bg-white rounded-xl px-4 py-3 text-slate-500 hover:text-slate-700 transition-colors text-sm">
              <Key className="w-4 h-4" />
              Send Login Invite / Reset Access
            </button>
            <p className="text-xs text-slate-500">Sends a new login link to the user's email.</p>
          </div>
        </div>

        {/* Footer Buttons */}
        <div className="flex gap-3 p-6 pt-4 border-t border-white/5 flex-shrink-0">
          <button
            onClick={onClose}
            className="flex-1 py-3 bg-white hover:bg-slate-100 text-slate-700 rounded-xl text-sm font-semibold transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex-1 py-3 bg-emerald-500 hover:bg-emerald-600 text-white rounded-xl text-sm font-semibold transition-colors disabled:opacity-50"
            data-testid="edit-user-save-button"
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}

// ── Admin Pages Editor ──
function AdminPagesEditor() {
  const pages = ['privacy-policy', 'terms-of-service', 'cookie-policy', 'security', 'about'];
  const [selectedSlug, setSelectedSlug] = useState(null);
  const [editContent, setEditContent] = useState('');
  const [editTitle, setEditTitle] = useState('');
  const [saving, setSaving] = useState(false);

  const { data: pageData, isLoading } = useQuery({
    queryKey: ['admin-page', selectedSlug],
    queryFn: async () => { const { data } = await api.get(`/api/pages/${selectedSlug}`); return data; },
    enabled: !!selectedSlug,
  });

  React.useEffect(() => {
    if (pageData) {
      setEditContent(pageData.content || '');
      setEditTitle(pageData.title || '');
    }
  }, [pageData]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put(`/api/admin/pages/${selectedSlug}`, { title: editTitle, content: editContent });
      toast.success('Page updated');
    } catch { toast.error('Failed to save'); }
    setSaving(false);
  };

  const pageLabels = {
    'privacy-policy': 'Privacy Policy', 'terms-of-service': 'Terms of Service',
    'cookie-policy': 'Cookie Policy', 'security': 'Security', 'about': 'About Us'
  };

  return (
    <div className="bg-[#131d35] rounded-2xl border border-white/5 overflow-hidden">
      <div className="p-5 border-b border-white/5">
        <h3 className="font-semibold text-white">CMS Page Editor</h3>
        <p className="text-sm text-slate-400 mt-1">Edit content for legal and informational pages</p>
      </div>
      <div className="flex flex-col lg:flex-row">
        {/* Sidebar */}
        <div className="w-full lg:w-56 border-b lg:border-b-0 lg:border-r border-white/5 p-4 space-y-1">
          {pages.map(slug => (
            <button key={slug} onClick={() => setSelectedSlug(slug)} className={`w-full text-left px-3 py-2.5 rounded-lg text-sm transition-colors ${selectedSlug === slug ? 'bg-emerald-500/20 text-emerald-400 font-medium' : 'text-slate-400 hover:text-white hover:bg-white/5'}`} data-testid={`admin-page-select-${slug}`}>
              {pageLabels[slug] || slug}
            </button>
          ))}
        </div>
        {/* Editor */}
        <div className="flex-1 p-5">
          {!selectedSlug ? (
            <div className="text-center py-16 text-slate-500"><FileText className="w-10 h-10 mx-auto mb-3 opacity-50" /><p className="text-sm">Select a page to edit</p></div>
          ) : isLoading ? (
            <div className="flex justify-center py-16"><div className="w-6 h-6 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" /></div>
          ) : (
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-white mb-1.5 block">Page Title</label>
                <input value={editTitle} onChange={e => setEditTitle(e.target.value)} className="w-full bg-[#1e293b] border border-white/10 rounded-xl px-4 py-2.5 text-white text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/50" data-testid="admin-page-title-input" />
              </div>
              <div>
                <label className="text-sm font-medium text-white mb-1.5 block">Content</label>
                <textarea value={editContent} onChange={e => setEditContent(e.target.value)} rows={18} className="w-full bg-[#1e293b] border border-white/10 rounded-xl px-4 py-3 text-white text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/50 resize-none font-mono leading-relaxed" data-testid="admin-page-content-input" />
              </div>
              <div className="flex justify-end">
                <button onClick={handleSave} disabled={saving} className="flex items-center gap-2 px-5 py-2.5 bg-emerald-500 hover:bg-emerald-600 text-white rounded-xl text-sm font-semibold transition-colors disabled:opacity-50" data-testid="admin-page-save-button">
                  <Save className="w-4 h-4" />{saving ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Admin Blogs Editor ──
function AdminBlogsEditor({ blogs, refetchBlogs }) {
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ title: '', excerpt: '', content: '', category: 'general', author: 'ExpensIQ Team', status: 'draft' });
  const [saving, setSaving] = useState(false);

  const openCreate = () => { setForm({ title: '', excerpt: '', content: '', category: 'general', author: 'ExpensIQ Team', status: 'draft' }); setCreating(true); setEditing(null); };
  const openEdit = (blog) => { setForm({ title: blog.title, excerpt: blog.excerpt, content: blog.content, category: blog.category, author: blog.author, status: blog.status }); setEditing(blog); setCreating(false); };
  const closeEditor = () => { setEditing(null); setCreating(false); };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (creating) {
        await api.post('/api/admin/blogs', form);
        toast.success('Blog created');
      } else {
        await api.put(`/api/admin/blogs/${editing.id}`, form);
        toast.success('Blog updated');
      }
      refetchBlogs();
      closeEditor();
    } catch { toast.error('Failed to save'); }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this blog post?')) return;
    try { await api.delete(`/api/admin/blogs/${id}`); toast.success('Deleted'); refetchBlogs(); } catch { toast.error('Failed to delete'); }
  };

  const categories = ['general', 'Tax Tips', 'Technology', 'Business Guide', 'Product Update', 'How-To'];

  return (
    <div className="bg-[#131d35] rounded-2xl border border-white/5 overflow-hidden">
      <div className="p-5 border-b border-white/5 flex items-center justify-between">
        <div><h3 className="font-semibold text-white">Blog Management</h3><p className="text-sm text-slate-400 mt-0.5">{blogs.length} post{blogs.length !== 1 ? 's' : ''}</p></div>
        <button onClick={openCreate} className="flex items-center gap-2 px-4 py-2 bg-emerald-500 hover:bg-emerald-600 text-white rounded-xl text-sm font-semibold transition-colors" data-testid="admin-blog-create-button">
          <Plus className="w-4 h-4" /> New Post
        </button>
      </div>

      {(creating || editing) ? (
        <div className="p-5 space-y-4">
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-white font-semibold">{creating ? 'New Blog Post' : 'Edit Blog Post'}</h4>
            <button onClick={closeEditor} className="text-slate-400 hover:text-white"><X className="w-5 h-5" /></button>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-white mb-1.5 block">Title</label>
              <input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} className="w-full bg-[#1e293b] border border-white/10 rounded-xl px-4 py-2.5 text-white text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/50" data-testid="admin-blog-title-input" />
            </div>
            <div>
              <label className="text-sm font-medium text-white mb-1.5 block">Category</label>
              <select value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))} className="w-full bg-[#1e293b] border border-white/10 rounded-xl px-4 py-2.5 text-white text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/50" data-testid="admin-blog-category-select">
                {categories.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-white mb-1.5 block">Author</label>
              <input value={form.author} onChange={e => setForm(f => ({ ...f, author: e.target.value }))} className="w-full bg-[#1e293b] border border-white/10 rounded-xl px-4 py-2.5 text-white text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/50" data-testid="admin-blog-author-input" />
            </div>
            <div>
              <label className="text-sm font-medium text-white mb-1.5 block">Status</label>
              <select value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value }))} className="w-full bg-[#1e293b] border border-white/10 rounded-xl px-4 py-2.5 text-white text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/50" data-testid="admin-blog-status-select">
                <option value="draft">Draft</option>
                <option value="published">Published</option>
              </select>
            </div>
          </div>
          <div>
            <label className="text-sm font-medium text-white mb-1.5 block">Excerpt</label>
            <input value={form.excerpt} onChange={e => setForm(f => ({ ...f, excerpt: e.target.value }))} className="w-full bg-[#1e293b] border border-white/10 rounded-xl px-4 py-2.5 text-white text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/50" placeholder="Brief description..." data-testid="admin-blog-excerpt-input" />
          </div>
          <div>
            <label className="text-sm font-medium text-white mb-1.5 block">Content</label>
            <textarea value={form.content} onChange={e => setForm(f => ({ ...f, content: e.target.value }))} rows={12} className="w-full bg-[#1e293b] border border-white/10 rounded-xl px-4 py-3 text-white text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/50 resize-none font-mono leading-relaxed" data-testid="admin-blog-content-input" />
          </div>
          <div className="flex justify-end gap-3">
            <button onClick={closeEditor} className="px-4 py-2 bg-white/10 hover:bg-white/15 text-slate-300 rounded-xl text-sm font-medium transition-colors">Cancel</button>
            <button onClick={handleSave} disabled={saving} className="flex items-center gap-2 px-5 py-2 bg-emerald-500 hover:bg-emerald-600 text-white rounded-xl text-sm font-semibold transition-colors disabled:opacity-50" data-testid="admin-blog-save-button">
              <Save className="w-4 h-4" />{saving ? 'Saving...' : creating ? 'Publish' : 'Save'}
            </button>
          </div>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="text-left text-xs text-slate-500 uppercase tracking-wider border-b border-white/5">
                <th className="px-5 py-3">Title</th>
                <th className="px-5 py-3">Category</th>
                <th className="px-5 py-3">Status</th>
                <th className="px-5 py-3">Date</th>
                <th className="px-5 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {blogs.map(blog => (
                <tr key={blog.id} className="hover:bg-white/[0.02] transition-colors" data-testid={`admin-blog-row-${blog.id}`}>
                  <td className="px-5 py-3.5">
                    <p className="text-sm font-medium text-white truncate max-w-[300px]">{blog.title}</p>
                    <p className="text-xs text-slate-500 truncate max-w-[300px]">{blog.excerpt}</p>
                  </td>
                  <td className="px-5 py-3.5"><span className="text-xs text-slate-400">{blog.category}</span></td>
                  <td className="px-5 py-3.5">
                    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${blog.status === 'published' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-amber-500/20 text-amber-400'}`}>
                      {blog.status === 'published' ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
                      {blog.status}
                    </span>
                  </td>
                  <td className="px-5 py-3.5 text-sm text-slate-400">{blog.created_at ? format(new Date(blog.created_at), 'dd MMM yyyy') : '-'}</td>
                  <td className="px-5 py-3.5">
                    <div className="flex items-center justify-end gap-1">
                      <button onClick={() => openEdit(blog)} className="p-1.5 rounded-lg text-slate-400 hover:text-blue-400 hover:bg-blue-500/10 transition-colors" title="Edit"><Edit className="w-4 h-4" /></button>
                      <button onClick={() => handleDelete(blog.id)} className="p-1.5 rounded-lg text-slate-400 hover:text-red-400 hover:bg-red-500/10 transition-colors" title="Delete"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </td>
                </tr>
              ))}
              {blogs.length === 0 && <tr><td colSpan={5} className="text-center py-10 text-slate-500">No blog posts yet</td></tr>}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── Admin Contacts List ──
function AdminContactsList({ contacts, refetchContacts }) {
  const handleMarkRead = async (id) => {
    try { await api.put(`/api/admin/contacts/${id}`, { status: 'read' }); toast.success('Marked as read'); refetchContacts(); } catch { toast.error('Failed'); }
  };

  return (
    <div className="bg-[#131d35] rounded-2xl border border-white/5 overflow-hidden">
      <div className="p-5 border-b border-white/5">
        <h3 className="font-semibold text-white">Contact Messages</h3>
        <p className="text-sm text-slate-400 mt-0.5">{contacts.length} message{contacts.length !== 1 ? 's' : ''}</p>
      </div>
      <div className="divide-y divide-white/5">
        {contacts.map(msg => (
          <div key={msg.id} className="p-5 hover:bg-white/[0.02] transition-colors" data-testid={`admin-contact-${msg.id}`}>
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-3 mb-1">
                  <p className="text-sm font-semibold text-white">{msg.name}</p>
                  <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${msg.status === 'new' ? 'bg-blue-500/20 text-blue-400' : 'bg-slate-500/20 text-slate-400'}`}>
                    {msg.status}
                  </span>
                </div>
                <p className="text-xs text-slate-500 mb-2">{msg.email} {msg.subject ? `· ${msg.subject}` : ''}</p>
                <p className="text-sm text-slate-300 leading-relaxed">{msg.message}</p>
                <p className="text-xs text-slate-600 mt-2">{msg.created_at ? format(new Date(msg.created_at), 'dd MMM yyyy HH:mm') : ''}</p>
              </div>
              {msg.status === 'new' && (
                <button onClick={() => handleMarkRead(msg.id)} className="flex-shrink-0 px-3 py-1.5 bg-white/10 hover:bg-white/15 text-slate-300 rounded-lg text-xs font-medium transition-colors" data-testid={`admin-contact-mark-read-${msg.id}`}>
                  Mark Read
                </button>
              )}
            </div>
          </div>
        ))}
        {contacts.length === 0 && <div className="text-center py-16 text-slate-500"><MessageSquare className="w-10 h-10 mx-auto mb-3 opacity-50" /><p className="text-sm">No messages yet</p></div>}
      </div>
    </div>
  );
}

// ── Admin Email Panel ──
function AdminEmailPanel() {
  const { data: gmailStatus, refetch: refetchGmail } = useQuery({
    queryKey: ['gmail-status'],
    queryFn: async () => { const { data } = await api.get('/api/admin/gmail/status'); return data; }
  });

  const [inviteForm, setInviteForm] = useState({ email: '', name: '' });
  const [resetEmail, setResetEmail] = useState('');
  const [inviteLoading, setInviteLoading] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  const handleConnectGmail = async () => {
    try {
      const { data } = await api.get('/api/admin/gmail/connect');
      window.location.href = data.auth_url;
    } catch {
      toast.error('Failed to start Gmail connection');
    }
  };

  const handleSendInvite = async () => {
    if (!inviteForm.email) { toast.error('Email is required'); return; }
    setInviteLoading(true);
    try {
      const { data } = await api.post('/api/admin/send-invite', inviteForm);
      setLastResult({ type: 'invite', ...data });
      toast.success(data.message);
      setInviteForm({ email: '', name: '' });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send invite');
    }
    setInviteLoading(false);
  };

  const handleResetAccess = async () => {
    if (!resetEmail) { toast.error('Email is required'); return; }
    if (!window.confirm(`Reset password for ${resetEmail}?`)) return;
    setResetLoading(true);
    try {
      const { data } = await api.post('/api/admin/reset-access', { email: resetEmail });
      setLastResult({ type: 'reset', ...data });
      toast.success(data.message);
      setResetEmail('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reset access');
    }
    setResetLoading(false);
  };

  return (
    <div className="space-y-6">
      {/* Gmail Connection Status */}
      <div className="bg-[#131d35] rounded-2xl border border-white/5 p-5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${gmailStatus?.connected ? 'bg-emerald-500/20' : 'bg-amber-500/20'}`}>
              <Mail className={`w-5 h-5 ${gmailStatus?.connected ? 'text-emerald-400' : 'text-amber-400'}`} />
            </div>
            <div>
              <h3 className="font-semibold text-white">Gmail Integration</h3>
              <p className="text-sm text-slate-400">
                {gmailStatus?.connected
                  ? `Connected ${gmailStatus.connected_at ? '· ' + format(new Date(gmailStatus.connected_at), 'dd MMM yyyy') : ''}`
                  : 'Connect Gmail to send invite & reset emails'}
              </p>
            </div>
          </div>
          <button
            onClick={handleConnectGmail}
            className={`px-4 py-2 rounded-xl text-sm font-semibold transition-colors flex items-center gap-2 ${gmailStatus?.connected ? 'bg-white/10 hover:bg-white/15 text-slate-300' : 'bg-emerald-500 hover:bg-emerald-600 text-white'}`}
            data-testid="gmail-connect-button"
          >
            <Link2 className="w-4 h-4" />
            {gmailStatus?.connected ? 'Reconnect' : 'Connect Gmail'}
          </button>
        </div>
        {!gmailStatus?.connected && (
          <div className="mt-4 flex items-start gap-2 p-3 bg-amber-500/10 rounded-xl border border-amber-500/20">
            <AlertCircle className="w-4 h-4 text-amber-400 mt-0.5 flex-shrink-0" />
            <p className="text-xs text-amber-300">Without Gmail connected, invites and resets will generate credentials but not send emails. You'll need to share credentials manually.</p>
          </div>
        )}
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Send Login Invite */}
        <div className="bg-[#131d35] rounded-2xl border border-white/5 p-5">
          <div className="flex items-center gap-2 mb-4">
            <Send className="w-5 h-5 text-emerald-400" />
            <h3 className="font-semibold text-white">Send Login Invite</h3>
          </div>
          <p className="text-sm text-slate-400 mb-4">Create a new user account and send them login credentials.</p>
          <div className="space-y-3">
            <div>
              <label className="text-xs font-medium text-slate-400 mb-1 block">Name</label>
              <input value={inviteForm.name} onChange={e => setInviteForm(f => ({ ...f, name: e.target.value }))} placeholder="John Smith" className="w-full bg-[#1e293b] border border-white/10 rounded-xl px-4 py-2.5 text-white text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/50" data-testid="invite-name-input" />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-400 mb-1 block">Email *</label>
              <input type="email" value={inviteForm.email} onChange={e => setInviteForm(f => ({ ...f, email: e.target.value }))} placeholder="john@example.com" className="w-full bg-[#1e293b] border border-white/10 rounded-xl px-4 py-2.5 text-white text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/50" data-testid="invite-email-input" />
            </div>
            <button onClick={handleSendInvite} disabled={inviteLoading} className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-emerald-500 hover:bg-emerald-600 text-white rounded-xl text-sm font-semibold transition-colors disabled:opacity-50" data-testid="send-invite-button">
              {inviteLoading ? <><Loader2 className="w-4 h-4 animate-spin" /> Sending...</> : <><Send className="w-4 h-4" /> Send Invite</>}
            </button>
          </div>
        </div>

        {/* Reset Access */}
        <div className="bg-[#131d35] rounded-2xl border border-white/5 p-5">
          <div className="flex items-center gap-2 mb-4">
            <RefreshCw className="w-5 h-5 text-blue-400" />
            <h3 className="font-semibold text-white">Reset User Access</h3>
          </div>
          <p className="text-sm text-slate-400 mb-4">Reset a user's password and send them new credentials.</p>
          <div className="space-y-3">
            <div>
              <label className="text-xs font-medium text-slate-400 mb-1 block">User Email *</label>
              <input type="email" value={resetEmail} onChange={e => setResetEmail(e.target.value)} placeholder="user@example.com" className="w-full bg-[#1e293b] border border-white/10 rounded-xl px-4 py-2.5 text-white text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/50" data-testid="reset-email-input" />
            </div>
            <button onClick={handleResetAccess} disabled={resetLoading} className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-blue-500 hover:bg-blue-600 text-white rounded-xl text-sm font-semibold transition-colors disabled:opacity-50" data-testid="reset-access-button">
              {resetLoading ? <><Loader2 className="w-4 h-4 animate-spin" /> Resetting...</> : <><RefreshCw className="w-4 h-4" /> Reset Access</>}
            </button>
          </div>
        </div>
      </div>

      {/* Last Result */}
      {lastResult && (
        <div className="bg-[#131d35] rounded-2xl border border-white/5 p-5" data-testid="email-result-panel">
          <div className="flex items-center gap-2 mb-3">
            <CheckCircle className="w-5 h-5 text-emerald-400" />
            <h3 className="font-semibold text-white">{lastResult.type === 'invite' ? 'Invite Sent' : 'Access Reset'}</h3>
          </div>
          <p className="text-sm text-slate-300 mb-3">{lastResult.message}</p>
          {lastResult.sent ? (
            <p className="text-sm text-emerald-400 flex items-center gap-1.5"><Mail className="w-4 h-4" /> Email delivered via Gmail</p>
          ) : (
            <div className="bg-amber-500/10 border border-amber-500/20 rounded-xl p-4">
              <p className="text-sm text-amber-300 mb-2 flex items-center gap-1.5"><AlertCircle className="w-4 h-4" /> Gmail not connected — share these credentials manually:</p>
              {lastResult.temp_password && (
                <div className="bg-[#1e293b] rounded-lg p-3 font-mono text-sm text-white">
                  <p>Temporary Password: <span className="text-emerald-400 font-bold">{lastResult.temp_password}</span></p>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Main Admin Dashboard ──
export default function AdminDashboard() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [userSearch, setUserSearch] = useState('');
  const [editingUser, setEditingUser] = useState(null);
  const [cmsTab, setCmsTab] = useState(null); // for page/blog editing

  const { data: stats = {} } = useQuery({
    queryKey: ['admin-stats'],
    queryFn: async () => { const { data } = await api.get('/api/admin/stats'); return data; }
  });

  const { data: users = [] } = useQuery({
    queryKey: ['admin-users'],
    queryFn: async () => { const { data } = await api.get('/api/admin/users'); return data; }
  });

  const { data: plans = [] } = useQuery({
    queryKey: ['admin-plans'],
    queryFn: async () => { const { data } = await api.get('/api/admin/plans'); return data; }
  });

  const { data: receipts = [] } = useQuery({
    queryKey: ['receipts'],
    queryFn: async () => { const { data } = await api.get('/api/receipts?sort=-date&limit=500'); return data; }
  });

  const { data: blogs = [], refetch: refetchBlogs } = useQuery({
    queryKey: ['admin-blogs'],
    queryFn: async () => { const { data } = await api.get('/api/blogs?status='); return data; }
  });

  const { data: contacts = [], refetch: refetchContacts } = useQuery({
    queryKey: ['admin-contacts'],
    queryFn: async () => { const { data } = await api.get('/api/admin/contacts'); return data; }
  });

  const deleteUserMutation = useMutation({
    mutationFn: (id) => api.delete(`/api/admin/users/${id}`),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['admin-users'] }); queryClient.invalidateQueries({ queryKey: ['admin-stats'] }); toast.success('User deleted'); }
  });

  const updateUserMutation = useMutation({
    mutationFn: ({ id, data }) => api.put(`/api/admin/users/${id}`, data),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['admin-users'] }); toast.success('User updated'); }
  });

  const updateStatusMutation = useMutation({
    mutationFn: ({ id, status }) => api.put(`/api/admin/users/${id}/status`, { status }),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['admin-users'] }); toast.success('Status updated'); }
  });

  // Storage formatting
  const formatStorage = (bytes) => {
    if (bytes >= 1e12) return `${(bytes / 1e12).toFixed(1)} TB`;
    if (bytes >= 1e9) return `${(bytes / 1e9).toFixed(1)} GB`;
    if (bytes >= 1e6) return `${(bytes / 1e6).toFixed(1)} MB`;
    if (bytes >= 1e3) return `${(bytes / 1e3).toFixed(1)} KB`;
    return `${bytes} B`;
  };

  // Daily uploads chart data
  const dailyUploads = Array.from({ length: 30 }, (_, i) => {
    const d = new Date(); d.setDate(d.getDate() - (29 - i));
    const dayStr = format(d, 'yyyy-MM-dd');
    const count = receipts.filter(r => r.date === dayStr || r.created_at?.startsWith(dayStr)).length;
    return { day: format(d, 'dd'), date: format(d, 'MMM dd'), uploads: count };
  });

  // Storage distribution
  const storageData = [
    { name: 'Receipts', value: stats.receipts || 0 },
    { name: 'Documents', value: stats.documents || 0 },
    { name: 'Invoices', value: stats.invoices || 0 },
  ];

  // User plan distribution
  const planDist = [
    { name: 'Individual', value: Math.max(1, Math.floor((stats.users || 1) * 0.5)), color: '#10b981' },
    { name: 'Basic', value: Math.max(0, Math.floor((stats.users || 1) * 0.3)), color: '#3b82f6' },
    { name: 'Standard', value: Math.max(0, Math.floor((stats.users || 1) * 0.2)), color: '#8b5cf6' },
  ];

  const filteredUsers = users.filter(u =>
    !userSearch || u.name?.toLowerCase().includes(userSearch.toLowerCase()) || u.email?.toLowerCase().includes(userSearch.toLowerCase())
  );

  const statCards = [
    { title: 'Active Users', value: stats.users || 0, sub: '+12% this month', icon: Users, iconBg: 'bg-cyan-500/20', iconColor: 'text-cyan-400' },
    { title: 'Total Uploads', value: (stats.receipts || 0) + (stats.documents || 0), sub: `${stats.file_count || 0} files stored`, icon: DollarSign, iconBg: 'bg-cyan-500/20', iconColor: 'text-cyan-400' },
    { title: 'Storage Used', value: formatStorage(stats.storage_bytes || 0), sub: 'of 10 GB capacity', icon: HardDrive, iconBg: 'bg-purple-500/20', iconColor: 'text-purple-400' },
    { title: 'System Uptime', value: '99.9%', sub: 'Response: 45ms', icon: Activity, iconBg: 'bg-amber-500/20', iconColor: 'text-amber-400' },
  ];

  // Exact plan features matching design
  const planCards = [
    {
      name: 'Individual', subtitle: 'Free personal expense tracking',
      price: 0, interval: 'month', storage: '1GB storage', popular: false,
      features: [
        '25 Smart Scans per month',
        'Smart auto-categorisation',
        'Basic document vault',
        'Secure cloud backup',
        'Spending overview dashboard',
      ]
    },
    {
      name: 'Basic', subtitle: 'For individuals & small teams',
      price: 4.95, interval: 'month', storage: '10GB storage', popular: true,
      features: [
        'Unlimited Smart Scans',
        'AI Spending Insights',
        'Tax Mode with ATO categories',
        'Distance & mileage tracking',
        'Up to 2 users included',
        'Budget tracking & alerts',
        'Bill & subscription management',
        'Document vault (10GB storage)',
        'Add up to 5 extra users at $4.95/user/month',
      ]
    },
    {
      name: 'Standard', subtitle: 'Everything in Basic, built for teams',
      price: 14.95, interval: 'month', storage: '100GB storage', popular: false,
      features: [
        'Everything in Basic',
        'Up to 10 users included',
        'Employee expense submissions & approvals',
        'Client & project tagging',
        'Exportable expense reports',
        'Accountant collaboration tools',
        'BAS-ready GST summaries',
        'Invoice management',
        'Priority support',
        'Add up to 5 extra users at $9.95/user/month',
      ]
    },
  ];

  return (
    <div className="min-h-screen bg-[#0f1729]" data-testid="admin-dashboard-page">
      {/* Header */}
      <div className="bg-[#131d35] border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="bg-gradient-to-br from-emerald-500 to-teal-600 p-2 rounded-xl"><ScanLine className="w-5 h-5 text-white" /></div>
            <div><span className="text-lg font-bold text-white">ExpensIQ</span><span className="text-[9px] font-medium text-slate-400 tracking-widest ml-1">AUSTRALIA</span></div>
            <span className="text-white/60 ml-2">Admin Dashboard</span>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2 bg-emerald-500/15 text-emerald-400 px-3 py-1.5 rounded-full text-sm font-medium"><Heart className="w-4 h-4" />System Healthy</div>
            <button className="p-2 text-slate-400 hover:text-white transition-colors"><Bell className="w-5 h-5" /></button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stat Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {statCards.map((stat, i) => (
            <motion.div key={stat.title} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.08 }} className="bg-[#131d35] rounded-2xl p-5 border border-white/5" data-testid={`admin-stat-${stat.title.toLowerCase().replace(/\s+/g, '-')}`}>
              <div className="flex items-start justify-between mb-3"><div className={`p-2.5 rounded-xl ${stat.iconBg}`}><stat.icon className={`w-5 h-5 ${stat.iconColor}`} /></div></div>
              <p className="text-sm text-slate-400">{stat.title}</p>
              <p className="text-2xl font-bold text-white mt-1">{stat.value}</p>
              <p className="text-xs text-slate-500 mt-1">{stat.sub}</p>
            </motion.div>
          ))}
        </div>

        {/* Tabs */}
        <Tabs defaultValue="analytics">
          <TabsList className="bg-[#131d35] border border-white/5 w-full mb-6 h-12">
            <TabsTrigger value="analytics" className="flex-1 text-slate-400 data-[state=active]:bg-white/10 data-[state=active]:text-white" data-testid="admin-tab-analytics"><BarChart3 className="w-4 h-4 mr-2" />Analytics</TabsTrigger>
            <TabsTrigger value="subscriptions" className="flex-1 text-slate-400 data-[state=active]:bg-white/10 data-[state=active]:text-white" data-testid="admin-tab-subscriptions"><CreditCard className="w-4 h-4 mr-2" />Subscriptions</TabsTrigger>
            <TabsTrigger value="users" className="flex-1 text-slate-400 data-[state=active]:bg-white/10 data-[state=active]:text-white" data-testid="admin-tab-users"><UserCog className="w-4 h-4 mr-2" />Users</TabsTrigger>
            <TabsTrigger value="plans" className="flex-1 text-slate-400 data-[state=active]:bg-white/10 data-[state=active]:text-white" data-testid="admin-tab-plans"><Layers className="w-4 h-4 mr-2" />Plan Editor</TabsTrigger>
            <TabsTrigger value="health" className="flex-1 text-slate-400 data-[state=active]:bg-white/10 data-[state=active]:text-white" data-testid="admin-tab-health"><Heart className="w-4 h-4 mr-2" />Health</TabsTrigger>
            <TabsTrigger value="pages" className="flex-1 text-slate-400 data-[state=active]:bg-white/10 data-[state=active]:text-white" data-testid="admin-tab-pages"><FileText className="w-4 h-4 mr-2" />Pages</TabsTrigger>
            <TabsTrigger value="blogs" className="flex-1 text-slate-400 data-[state=active]:bg-white/10 data-[state=active]:text-white" data-testid="admin-tab-blogs"><BookOpen className="w-4 h-4 mr-2" />Blogs</TabsTrigger>
            <TabsTrigger value="contacts" className="flex-1 text-slate-400 data-[state=active]:bg-white/10 data-[state=active]:text-white" data-testid="admin-tab-contacts"><MessageSquare className="w-4 h-4 mr-2" />Contacts</TabsTrigger>
            <TabsTrigger value="email" className="flex-1 text-slate-400 data-[state=active]:bg-white/10 data-[state=active]:text-white" data-testid="admin-tab-email"><Mail className="w-4 h-4 mr-2" />Email</TabsTrigger>
          </TabsList>

          {/* ── Analytics Tab ── */}
          <TabsContent value="analytics" data-testid="admin-analytics-tab">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="bg-[#131d35] rounded-2xl p-6 border border-white/5">
                <div className="flex items-center gap-2 mb-6"><div className="p-1.5 bg-emerald-500/20 rounded-lg"><BarChart3 className="w-4 h-4 text-emerald-400" /></div><h3 className="font-semibold text-white">Daily Uploads (30 days)</h3></div>
                <ResponsiveContainer width="100%" height={260}>
                  <AreaChart data={dailyUploads}>
                    <defs><linearGradient id="adminGrad" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#10b981" stopOpacity={0.4} /><stop offset="95%" stopColor="#10b981" stopOpacity={0} /></linearGradient></defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" vertical={false} />
                    <XAxis dataKey="day" axisLine={false} tickLine={false} tick={{ fill: '#64748b', fontSize: 11 }} />
                    <YAxis axisLine={false} tickLine={false} tick={{ fill: '#64748b', fontSize: 11 }} allowDecimals={false} />
                    <Tooltip contentStyle={{ backgroundColor: '#1e293b', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '12px', color: '#e2e8f0' }} />
                    <Area type="monotone" dataKey="uploads" stroke="#10b981" strokeWidth={2} fillOpacity={1} fill="url(#adminGrad)" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
              <div className="bg-[#131d35] rounded-2xl p-6 border border-white/5">
                <div className="flex items-center gap-2 mb-6"><div className="p-1.5 bg-purple-500/20 rounded-lg"><HardDrive className="w-4 h-4 text-purple-400" /></div><h3 className="font-semibold text-white">Storage Distribution</h3></div>
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart data={storageData} layout="vertical" barCategoryGap="30%">
                    <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" horizontal={false} />
                    <XAxis type="number" axisLine={false} tickLine={false} tick={{ fill: '#64748b', fontSize: 11 }} />
                    <YAxis type="category" dataKey="name" axisLine={false} tickLine={false} tick={{ fill: '#94a3b8', fontSize: 12 }} width={90} />
                    <Tooltip contentStyle={{ backgroundColor: '#1e293b', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '12px', color: '#e2e8f0' }} />
                    <Bar dataKey="value" fill="#8b5cf6" radius={[0, 8, 8, 0]} barSize={28} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </TabsContent>

          {/* ── Subscriptions Tab ── */}
          <TabsContent value="subscriptions" data-testid="admin-subscriptions-tab">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="bg-[#131d35] rounded-2xl p-6 border border-white/5">
                <h3 className="font-semibold text-white mb-4">User Plans Distribution</h3>
                <ResponsiveContainer width="100%" height={280}>
                  <PieChart><Pie data={planDist} cx="50%" cy="50%" innerRadius={60} outerRadius={100} paddingAngle={3} dataKey="value">{planDist.map((entry, i) => <Cell key={i} fill={entry.color} />)}</Pie><Tooltip contentStyle={{ backgroundColor: '#1e293b', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '12px', color: '#e2e8f0' }} /></PieChart>
                </ResponsiveContainer>
                <div className="flex justify-center gap-6 mt-2">{planDist.map(p => <div key={p.name} className="flex items-center gap-2"><div className="w-3 h-3 rounded-full" style={{ backgroundColor: p.color }} /><span className="text-sm text-slate-400">{p.name} ({p.value})</span></div>)}</div>
              </div>
              <div className="bg-[#131d35] rounded-2xl p-6 border border-white/5">
                <h3 className="font-semibold text-white mb-6">Subscription Metrics</h3>
                <div className="grid grid-cols-2 gap-4">
                  {[
                    { label: 'Monthly Revenue', value: fmt(planDist[1].value * 4.95 + planDist[2].value * 14.95), color: 'text-emerald-400' },
                    { label: 'Active Subscriptions', value: planDist[1].value + planDist[2].value, color: 'text-blue-400' },
                    { label: 'Churn Rate', value: '2.3%', color: 'text-amber-400' },
                    { label: 'Trial Conversions', value: '68%', color: 'text-purple-400' },
                  ].map(m => <div key={m.label} className="bg-white/5 rounded-xl p-4"><p className="text-xs text-slate-500 uppercase tracking-wider">{m.label}</p><p className={`text-xl font-bold mt-1 ${m.color}`}>{m.value}</p></div>)}
                </div>
              </div>
            </div>
          </TabsContent>

          {/* ── Users Tab ── */}
          <TabsContent value="users" data-testid="admin-users-tab">
            <div className="bg-[#131d35] rounded-2xl border border-white/5 overflow-hidden">
              <div className="p-5 border-b border-white/5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                <h3 className="font-semibold text-white">User Management</h3>
                <div className="relative w-full sm:w-64"><Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" /><input value={userSearch} onChange={(e) => setUserSearch(e.target.value)} placeholder="Search users..." className="w-full pl-10 pr-3 py-2 bg-white/5 border border-white/10 rounded-xl text-sm text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/50" data-testid="admin-user-search" /></div>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="text-left text-xs text-slate-500 uppercase tracking-wider border-b border-white/5">
                      <th className="px-5 py-3">User</th>
                      <th className="px-5 py-3">Role</th>
                      <th className="px-5 py-3">Status</th>
                      <th className="px-5 py-3">Plan</th>
                      <th className="px-5 py-3">Joined</th>
                      <th className="px-5 py-3 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/5">
                    {filteredUsers.map(u => (
                      <tr key={u.id} className="hover:bg-white/[0.02] transition-colors" data-testid={`admin-user-row-${u.id}`}>
                        <td className="px-5 py-3.5">
                          <div className="flex items-center gap-3">
                            <div className="w-9 h-9 rounded-full bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center text-white font-semibold text-sm flex-shrink-0">{u.name?.[0]?.toUpperCase() || 'U'}</div>
                            <div><p className="text-sm font-medium text-white">{u.name}</p><p className="text-xs text-slate-500">{u.email}</p></div>
                          </div>
                        </td>
                        <td className="px-5 py-3.5"><span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ${u.role === 'admin' ? 'bg-purple-500/20 text-purple-400' : 'bg-blue-500/20 text-blue-400'}`}><Shield className="w-3 h-3" />{u.role || 'user'}</span></td>
                        <td className="px-5 py-3.5"><span className={`inline-flex items-center gap-1.5 text-xs font-medium ${u.status === 'disabled' ? 'text-red-400' : 'text-emerald-400'}`}><span className={`w-2 h-2 rounded-full ${u.status === 'disabled' ? 'bg-red-400' : 'bg-emerald-400'}`} />{u.status === 'disabled' ? 'Disabled' : 'Active'}</span></td>
                        <td className="px-5 py-3.5"><span className="text-xs text-slate-400 capitalize">{u.plan || 'individual'}</span></td>
                        <td className="px-5 py-3.5 text-sm text-slate-400">{u.created_at ? format(new Date(u.created_at), 'dd MMM yyyy') : '-'}</td>
                        <td className="px-5 py-3.5">
                          <div className="flex items-center justify-end gap-1">
                            <button onClick={() => setEditingUser(u)} className="p-1.5 rounded-lg text-slate-400 hover:text-blue-400 hover:bg-blue-500/10 transition-colors" title="Edit user"><Edit className="w-4 h-4" /></button>
                            <button onClick={() => updateStatusMutation.mutate({ id: u.id, status: u.status === 'disabled' ? 'active' : 'disabled' })} className="p-1.5 rounded-lg text-slate-400 hover:text-amber-400 hover:bg-amber-500/10 transition-colors" title={u.status === 'disabled' ? 'Enable' : 'Disable'}>{u.status === 'disabled' ? <Check className="w-4 h-4" /> : <Ban className="w-4 h-4" />}</button>
                            <button onClick={() => { if (window.confirm(`Delete ${u.name}?`)) deleteUserMutation.mutate(u.id); }} className="p-1.5 rounded-lg text-slate-400 hover:text-red-400 hover:bg-red-500/10 transition-colors" title="Delete"><Trash2 className="w-4 h-4" /></button>
                          </div>
                        </td>
                      </tr>
                    ))}
                    {filteredUsers.length === 0 && <tr><td colSpan={6} className="text-center py-10 text-slate-500">No users found</td></tr>}
                  </tbody>
                </table>
              </div>
            </div>
          </TabsContent>

          {/* ── Plan Editor Tab ── */}
          <TabsContent value="plans" data-testid="admin-plans-tab">
            <p className="text-slate-400 text-sm mb-6">Edit pricing and features for each plan. Changes are saved locally.</p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {planCards.map((plan, i) => (
                <motion.div key={plan.name} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.1 }} className={`relative bg-[#131d35] rounded-2xl border overflow-hidden flex flex-col ${plan.popular ? 'border-emerald-500' : 'border-white/10'}`} data-testid={`admin-plan-card-${plan.name.toLowerCase()}`}>
                  {plan.popular && (
                    <div className="flex justify-center -mt-px">
                      <div className="bg-emerald-500 px-5 py-1.5 rounded-b-xl text-xs font-semibold text-white flex items-center gap-1.5">
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" /></svg>
                        Most Popular
                      </div>
                    </div>
                  )}
                  <div className="p-6 flex flex-col flex-1">
                    {/* Header with Edit */}
                    <div className="flex items-start justify-between mb-1">
                      <h3 className="text-xl font-bold text-white">{plan.name}</h3>
                      <button className="flex items-center gap-1.5 px-3 py-1.5 bg-white/10 hover:bg-white/15 rounded-lg text-slate-300 text-xs font-medium transition-colors" data-testid={`admin-plan-edit-${plan.name.toLowerCase()}`}>
                        <Edit className="w-3.5 h-3.5" />Edit
                      </button>
                    </div>
                    <p className="text-sm text-slate-400 mb-4">{plan.subtitle}</p>

                    {/* Price Box */}
                    <div className="bg-white/5 rounded-xl p-4 mb-5 border border-white/5">
                      <div className="flex items-baseline gap-1">
                        <span className="text-3xl font-bold text-white">${plan.price === 0 ? '0' : plan.price.toFixed(2)}</span>
                        <span className="text-slate-400 text-sm">/{plan.interval}</span>
                      </div>
                      <p className="text-sm text-slate-500 mt-1">{plan.storage}</p>
                    </div>

                    {/* Features */}
                    <div className="space-y-3 flex-1">
                      {plan.features.map((feature, fi) => (
                        <div key={fi} className="flex items-start gap-2.5">
                          <Check className="w-4 h-4 text-emerald-400 mt-0.5 flex-shrink-0" />
                          <span className="text-sm text-slate-300">{feature}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </TabsContent>

          {/* ── System Health Tab ── */}
          <TabsContent value="health" data-testid="admin-health-tab">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="bg-[#131d35] rounded-2xl p-6 border border-white/5">
                <div className="flex items-center gap-2 mb-6"><Heart className="w-5 h-5 text-emerald-400" /><h3 className="font-semibold text-white">Service Status</h3></div>
                <div className="space-y-3">
                  {['API Server', 'Database (MongoDB)', 'File Storage', 'AI Processing (GPT-5.2)', 'Authentication'].map(svc => (
                    <div key={svc} className="flex items-center justify-between py-3 px-4 bg-white/[0.02] rounded-xl"><span className="text-sm text-slate-300">{svc}</span><span className="flex items-center gap-1.5 text-xs font-medium text-emerald-400"><span className="w-2 h-2 rounded-full bg-emerald-400" />Operational</span></div>
                  ))}
                </div>
              </div>
              <div className="bg-[#131d35] rounded-2xl p-6 border border-white/5">
                <div className="flex items-center gap-2 mb-6"><Activity className="w-5 h-5 text-blue-400" /><h3 className="font-semibold text-white">Performance Metrics</h3></div>
                <div className="space-y-4">
                  {[
                    { label: 'API Response Time', value: '45ms', pct: 15, bg: 'bg-emerald-500', color: 'text-emerald-400' },
                    { label: 'Database Queries', value: '12ms avg', pct: 8, bg: 'bg-blue-500', color: 'text-blue-400' },
                    { label: 'Memory Usage', value: '256 MB', pct: 25, bg: 'bg-purple-500', color: 'text-purple-400' },
                    { label: 'CPU Usage', value: '12%', pct: 12, bg: 'bg-amber-500', color: 'text-amber-400' },
                    { label: 'Disk I/O', value: '3.2 MB/s', pct: 20, bg: 'bg-cyan-500', color: 'text-cyan-400' },
                  ].map(m => (
                    <div key={m.label}><div className="flex items-center justify-between mb-2"><span className="text-sm text-slate-400">{m.label}</span><span className={`text-sm font-medium ${m.color}`}>{m.value}</span></div><div className="h-1.5 bg-white/5 rounded-full overflow-hidden"><motion.div initial={{ width: 0 }} animate={{ width: `${m.pct}%` }} transition={{ duration: 1, delay: 0.2 }} className={`h-full rounded-full ${m.bg}`} /></div></div>
                  ))}
                </div>
              </div>
            </div>
          </TabsContent>

          {/* ── Pages CMS Tab ── */}
          <TabsContent value="pages" data-testid="admin-pages-tab">
            <AdminPagesEditor />
          </TabsContent>

          {/* ── Blogs CMS Tab ── */}
          <TabsContent value="blogs" data-testid="admin-blogs-tab">
            <AdminBlogsEditor blogs={blogs} refetchBlogs={refetchBlogs} />
          </TabsContent>

          {/* ── Contacts Tab ── */}
          <TabsContent value="contacts" data-testid="admin-contacts-tab">
            <AdminContactsList contacts={contacts} refetchContacts={refetchContacts} />
          </TabsContent>

          {/* ── Email / Invite Tab ── */}
          <TabsContent value="email" data-testid="admin-email-tab">
            <AdminEmailPanel />
          </TabsContent>
        </Tabs>
      </div>

      {/* Edit User Modal */}
      <AnimatePresence>
        {editingUser && (
          <EditUserModal
            user={editingUser}
            plans={plans}
            onClose={() => setEditingUser(null)}
            onSave={(data) => updateUserMutation.mutateAsync({ id: editingUser.id, data })}
          />
        )}
      </AnimatePresence>
    </div>
  );
}
